/**
 * GaugeChart — Velocímetro semicircular com faixas coloridas
 * Usado para ISP (Sálvia), OWI (RH), Adesão (Canal/RH)
 */
import { useMemo } from "react";

interface GaugeChartProps {
  value: number; // 0-100
  label: string;
  subtitle?: string;
  size?: number; // px, default 200
  ranges?: Array<{ min: number; max: number; color: string; label: string }>;
}

const DEFAULT_RANGES = [
  { min: 0, max: 40, color: "#DC2626", label: "Crítico" },
  { min: 40, max: 70, color: "#D97706", label: "Atenção" },
  { min: 70, max: 100, color: "#16A34A", label: "Saudável" },
];

export default function GaugeChart({ value, label, subtitle, size = 200, ranges = DEFAULT_RANGES }: GaugeChartProps) {
  const clampedValue = Math.max(0, Math.min(100, value));
  const radius = size * 0.4;
  const cx = size / 2;
  const cy = size * 0.55;
  const strokeWidth = size * 0.08;

  // Current range
  const currentRange = useMemo(() => {
    return ranges.find(r => clampedValue >= r.min && clampedValue < r.max) || ranges[ranges.length - 1];
  }, [clampedValue, ranges]);

  // Arc path helper (semicircle, left to right)
  const describeArc = (startAngle: number, endAngle: number) => {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  // Semicircle goes from 180° (left) to 0° (right)
  const totalAngle = 180;
  const startAngle = 180; // left

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox={`0 0 ${size} ${size * 0.65}`}>
        {/* Background arc segments by range */}
        {ranges.map((range, i) => {
          const rangeStart = startAngle - (range.min / 100) * totalAngle;
          const rangeEnd = startAngle - (range.max / 100) * totalAngle;
          return (
            <path
              key={i}
              d={describeArc(Math.min(rangeStart, rangeEnd), Math.max(rangeStart, rangeEnd))}
              fill="none"
              stroke={range.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              opacity={0.2}
            />
          );
        })}

        {/* Value arc */}
        {clampedValue > 0 && (
          <path
            d={describeArc(startAngle - (clampedValue / 100) * totalAngle, startAngle)}
            fill="none"
            stroke={currentRange.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}

        {/* Value text */}
        <text x={cx} y={cy - 8} textAnchor="middle" className="font-bold" style={{ fontSize: size * 0.18, fill: currentRange.color }}>
          {clampedValue}
        </text>
        <text x={cx} y={cy + size * 0.08} textAnchor="middle" style={{ fontSize: size * 0.065, fill: "#6B7280" }}>
          {currentRange.label}
        </text>
      </svg>
      <p className="text-sm font-semibold text-[#002F59] mt-1">{label}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
